import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { db } from "../firebase";
import { serverTimestamp, deleteDoc, Timestamp } from "firebase/firestore";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  orderBy,
  query,
} from "firebase/firestore";

// Helper function to convert Firestore Timestamp to ISO string
const convertTimestamp = (timestamp: Timestamp | string | undefined): string | undefined => {
  if (!timestamp) return undefined;
  if (typeof timestamp === "string") return timestamp;
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toISOString();
  }
  // Handle Firestore Timestamp object structure
  if (timestamp && typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as any).seconds * 1000).toISOString();
  }
  return undefined;
};

export interface Project {
  id?: string;
  name: string;
  description: string;
  category: string;
  imageUrl?: string;
  link_github?: string;
  link_demo?: string;
  link_demo_title: string;
  link_demo_broken?: boolean;
  link_github_broken?: boolean;
  createdAt?: Timestamp | string;
  updatedAt?: Timestamp | string;
  order?: number;
}

interface NewProjectPayload {
  name: string;
  description: string;
  category: string;
  imageUrl?: string;
  link_github?: string;
  link_demo?: string;
  link_demo_title: string;
  link_demo_broken?: boolean;
  link_github_broken?: boolean;
}

interface UpdateProjectPayload {
  id: string;
  name: string;
  description: string;
  category: string;
  imageUrl?: string;
  link_github?: string;
  link_demo?: string;
  link_demo_title: string;
  link_demo_broken?: boolean;
  link_github_broken?: boolean;
}

interface InitialState {
  projects: Project[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: InitialState = {
  projects: [],
  status: "idle",
  error: null,
};

export const fetchProjects = createAsyncThunk("projects/fetchProjects", async () => {
  try {
    // Fetch projects ordered by createdAt (newest first)
    let querySnapshot;
    try {
      const q = query(collection(db, "projects"), orderBy("createdAt", "desc"));
      querySnapshot = await getDocs(q);
    } catch (error: any) {
      // If ordering by createdAt fails (missing index), fetch all and sort client-side
      if (error.code === "failed-precondition" || error.message?.includes("index")) {
        console.warn("createdAt index not found, fetching all and sorting client-side");
        querySnapshot = await getDocs(collection(db, "projects"));
      } else {
        throw error;
      }
    }
    
    let projects: Project[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Skip documents that don't have required fields
      if (!data.name || !data.description || !data.category) {
        console.warn(`Skipping project ${doc.id}: missing required fields (name, description, or category)`);
        return;
      }
      projects.push({
        id: doc.id,
        name: data.name,
        description: data.description,
        category: data.category,
        imageUrl: data.imageUrl || "",
        link_github: data.link_github || "",
        link_demo: data.link_demo || "",
        link_demo_title: data.link_demo_title || "Demo",
        link_demo_broken: data.link_demo_broken || false,
        link_github_broken: data.link_github_broken || false,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        order: data.order || 0,
      } as Project);
    });

    // If we fetched without ordering, sort client-side by createdAt (newest first)
    // Note: createdAt is already converted to string by convertTimestamp above
    projects.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt as string).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt as string).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });

    return projects;
  } catch (error) {
    console.error("Error fetching projects:", error);
    throw error;
  }
});

export const addProject = createAsyncThunk(
  "projects/addProject",
  async (project: NewProjectPayload) => {
    const newProject = {
      ...project,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      order: 0,
    };
    const docRef = await addDoc(collection(db, "projects"), newProject);
    return {
      id: docRef.id,
      ...project,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as Project;
  }
);

export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async (payload: UpdateProjectPayload) => {
    const { id, ...updateData } = payload;
    const projectRef = doc(db, "projects", id);
    await updateDoc(projectRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
    return {
      id,
      ...updateData,
      updatedAt: new Date().toISOString(),
    } as Project;
  }
);

export const deleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (projectId: string) => {
    await deleteDoc(doc(db, "projects", projectId));
    return projectId;
  }
);

export const getProject = createAsyncThunk("projects/get", async (projectId: string) => {
  const projectDoc = await getDoc(doc(db, "projects", projectId));
  if (projectDoc.exists()) {
    const data = projectDoc.data();
    return {
      id: projectDoc.id,
      name: data.name,
      description: data.description,
      category: data.category,
      imageUrl: data.imageUrl || "",
      link_github: data.link_github || "",
      link_demo: data.link_demo || "",
      link_demo_title: data.link_demo_title || "Demo",
      link_demo_broken: data.link_demo_broken || false,
      link_github_broken: data.link_github_broken || false,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      order: data.order || 0,
    } as Project;
  } else {
    throw new Error("Project not found");
  }
});

const projectSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(addProject.fulfilled, (state, action) => {
        const existingProjectIndex = state.projects.findIndex(
          (project) => project.id === action.payload.id
        );
        if (existingProjectIndex >= 0) {
          return;
        }
        state.projects.push(action.payload);
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        const { id, ...updateData } = action.payload;
        const existingProject = state.projects.find((project) => project.id === id);
        if (existingProject) {
          Object.assign(existingProject, updateData);
          existingProject.updatedAt = new Date().toISOString();
        }
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter((project) => project.id !== action.payload);
      })
      .addCase(getProject.fulfilled, (state, action: PayloadAction<Project>) => {
        const index = state.projects.findIndex((project) => project.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        } else {
          state.projects.push(action.payload);
        }
      });
  },
});

export default projectSlice.reducer;

